import * as React from "react";
import StyledModal from "styled-react-modal";
import styled, { css } from "styled-components";

import {
  Colors,
  media
} from "src/library/utils";

import Grid from "../Hedron";
import { Text } from "../Text";
import { Button } from "../Button";
import { ButtonType } from "../Button/Button";
import { LoadingSpinner } from "../LoadingSpinner";
import HtmlBody from "../Notes/HtmlBody";
import { Icon } from "../Icon";
import { Icons } from "../Icon/types";
import ActionButton, { IAction } from "../Button/ActionButton";

export interface IModalActionProps {
  name: string;
  disabled?: boolean;
  action?: () => void;
  $buttonType?: ButtonType;
  background?: keyof Colors;
  actions?: IAction[];
}

export interface IModalProps {
  show: boolean;
  loading?: boolean;
  type?: "confirm" | "notice";
  title: string;
  body?: string;
  actions?: IModalActionProps[];
  content?: JSX.Element;
  backgroundAction: () => void;
  closeIcon?: boolean;
  small?: boolean;
}

// One small foible of this library - styling has to be declared in this way for the modal itself.
// Best to keep it as light and generic as possible - just base layout to allow for
// more theming in other parts.
const ModalWrapper = StyledModal.styled`
  position: relative;
  background: white;
  border-radius: 1rem;
  max-width: 100vw;
  min-height: 100px;
  opacity: 1;
  max-height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  .close-icon-wrapper {
    position: absolute;
    top: 10px;
    right: 10px;
  }
`;

const ModalLoadingStyled = styled.div`
  transition: ${({ theme }) => theme.transition.quicker};
  opacity: 1 !important;
  position: absolute;
  z-index: 99;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.4);

  * {
    box-sizing: content-box !important;
  }

  > div {
    position: relative;
    z-index: 2;

    > div {
      transition: ${({ theme }) => theme.transition.quicker};
      top: 0;
    }
  }
`;

const ModalBodyWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    > span {
      padding-bottom: ${theme.spacing.padding.sm};
    }

    padding: ${theme.spacing.padding.md};
  `}
`;

const ButtonWrapper = styled(Grid.Box)`
  ${({ theme, spacing }) => css`
    display: flex;
    flex-direction: row;
    justify-content: ${spacing};
    margin-top: ${theme.spacing.margins.sm};
    padding: ${theme.spacing.padding.md} ${theme.spacing.padding.md};
    border-top: 1px solid ${theme.colors.lightestGrey};

    button {
      
    }
    
    ${media({ lt: theme.breakpoints.sm })} {
      flex-direction: column;

      > div button {
        width: 100%;
        margin-bottom: ${theme.spacing.margins.sm};
      }
    }
  `}
`;

const ContentWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing.margins.sm};
  `}
`;

const ModalInner = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md};
    // max-width: 80vw;

    &.small {
      width: 38rem;
    }
  `}
`;

const Modal: React.ReactFCWithChildren<IModalProps> = ({
  show,
  loading,
  title,
  body,
  actions,
  backgroundAction,
  content,
  closeIcon,
  small
}) => {
  const isLoading = loading || false;

  return (
    <ModalWrapper
      onBackgroundClick={() => backgroundAction()}
      isOpen={show}
    >
      <ModalInner className={small ? "small" : ""}>
        {closeIcon && (
          <div
            onClick={backgroundAction}
            className="close-icon-wrapper"
          >
            <Icon icon={Icons.CLOSE} />
          </div>
        )}

        {isLoading && (
          <ModalLoadingStyled className="rt-loading">
            <LoadingSpinner type="circular" />
          </ModalLoadingStyled>
        )}

        <ModalBodyWrapper direction="vertical">
          <Text
            fontSize="h3"
            weight="bold"
          >
            {title}
          </Text>

          {body && (
            <HtmlBody
              body={body}
            />
          )}

          {content && (
            <ContentWrapper>
              {content}
            </ContentWrapper>
          )}
        </ModalBodyWrapper>

        {actions && (
          <ButtonWrapper spacing={actions && actions.length > 1 ? "space-between" : "flex-start"}>
            {actions.map((item, index) => item.actions ? (
              <ActionButton
                firstActionClickable
                key={index}
                title="actions"
                items={item.actions}
              />
            ) :
              (
                <Button
                  key={index}
                  disabled={item.disabled}
                  $buttonType={item.$buttonType}
                  background={item.background}
                  onClick={() => item?.action && item?.action()}
                  data-cypress={item.name.replace(/\s/, "-").toLowerCase()}
                >
                  {item.name}
                </Button>
              ))}
          </ButtonWrapper>
        )}
      </ModalInner>
    </ModalWrapper>
  );
};

Modal.defaultProps = { closeIcon: false };

export default Modal;
